import React, { useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import SimpleEmojiCaptcha from "../components/SimpleEmojiCaptcha";
import logo from "../assets/photos/hlthiqLogin.png";
import healthcareImage from "../assets/photos/medical_stethoscope.jpeg";

import "../assets/Login.css";

const Login = () => {
  const navigate = useNavigate();
  const { login: contextLogin, loginWithMicrosoft } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // CAPTCHA ENABLED FOR PRODUCTION
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);
  const [loginStep, setLoginStep] = useState('email'); // 'email', 'microsoft', or 'credentials'
  const [forceUpdate, setForceUpdate] = useState(0); // Force re-render when captcha completes
  const [autoLoggingIn, setAutoLoggingIn] = useState(false); // Track auto-login state


  // Memoized handlers so they don't change on every render (typing password won't re-create them)
  const handleCaptchaComplete = useCallback((token) => {
    console.log('=== CAPTCHA COMPLETION START ===');
    console.log('Token received:', token);

    setCaptchaToken(token);
    setCaptchaError("");

    // Force component re-render to update button states
    setForceUpdate(prev => prev + 1);

    console.log('=== CAPTCHA COMPLETION END ===');
    console.log('✅ Captcha verification completed successfully');

    // Auto-login after captcha completion
    setTimeout(() => {
      performAutoLogin(token);
    }, 200); // Small delay to allow UI to update

  }, [email, password, loginStep]);

  // Auto-login function that triggers after captcha completion
  const performAutoLogin = useCallback((token) => {
    console.log('🔄 Checking auto-login prerequisites...');

    // Check if we have all required credentials for auto-login
    const hasEmail = email && email.trim();
    const hasPassword = loginStep === 'credentials' ? password && password.trim() : true;
    const hasCaptcha = token;

    console.log('Auto-login prerequisites:', {
      hasEmail,
      hasPassword,
      hasCaptcha,
      loginStep,
      email: email,
      passwordLength: password ? password.length : 0
    });

    if (!hasEmail || !hasPassword || !hasCaptcha) {
      console.log('❌ Auto-login prerequisites not met');
      return;
    }

    console.log('✅ All prerequisites met, proceeding with auto-login');

    // Set loading state to show user something is happening
    setLoading(true);
    setAutoLoggingIn(true);
    setError("");

    // Trigger the appropriate login handler based on login step
    if (loginStep === 'microsoft') {
      console.log('🚀 Auto-login with Microsoft');
      handleMicrosoftLogin(token); // Pass token directly
    } else if (loginStep === 'credentials') {
      console.log('🚀 Auto-login with credentials');
      // Create a synthetic event for form submission
      const syntheticEvent = { preventDefault: () => {} };
      handleSubmit(syntheticEvent, token); // Pass token directly
    }
  }, [email, password, loginStep]);

  const handleCaptchaError = useCallback((error) => {
    setCaptchaError(error);
    setCaptchaToken(null);
  }, []);

  const handleOpenCaptcha = useCallback(() => {
    setShowCaptchaModal(true);
    setCaptchaError("");
  }, []);

  const handleCloseCaptcha = useCallback(() => {
    setShowCaptchaModal(false);
    setCaptchaError("");
  }, []);

  // Handle email input change (no real-time detection)
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Keep login step as 'email' until user submits
  };

  // Handle email form submission
  const handleEmailSubmit = (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }

    // Determine login step based on email domain
    const domain = email.toLowerCase().split('@')[1];
    if (domain === 'hlthiq.com') {
      setLoginStep('microsoft');
    } else {
      setLoginStep('credentials');
    }
  };

  // Handle Enter key in email field
  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEmailSubmit(e);
    }
  };

  // Login handler - uses AuthContext's login function
  const handleSubmit = async (e, tokenOverride = null) => {
    e.preventDefault();

    console.log('Form submission started');

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    // For credentials login, password is required
    if (loginStep === 'credentials' && !password) {
      setError("Please enter your password");
      return;
    }

    // For Microsoft login, don't allow email/password submission
    if (loginStep === 'microsoft') {
      setError("Please use the Microsoft login button below");
      return;
    }

    // CAPTCHA ENABLED FOR PRODUCTION
    // STRICTLY REQUIRE CAPTCHA VERIFICATION
    const currentCaptchaToken = tokenOverride || captchaToken;
    if (!currentCaptchaToken) {
      console.log('No captcha token found, cannot proceed with login');
      setError("Please complete the security verification first.");
      return;
    }

    console.log('✅ All requirements met, proceeding with login');

    // Additional validation to ensure everything is ready
    if (!email.trim() || !password.trim() || !currentCaptchaToken) {
      console.error('Login validation failed:', {
        email: !!email.trim(),
        password: !!password.trim(),
        captcha: !!currentCaptchaToken
      });
      setError("Missing required information. Please check all fields and complete verification.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Use the login function from AuthContext
      const response = await contextLogin({
        email: email.trim(),
        password: password.trim(),
        captchaToken: currentCaptchaToken
      });

      // Debug: Log the request details
      console.log('Login request starting:', {
        email: email.trim(),
        hasPassword: !!password.trim(),
        hasCaptcha: !!currentCaptchaToken,
        loginStep,
        captchaToken: currentCaptchaToken ? 'present' : 'missing'
      });

      if (response.success) {
        // Check if password change is required
        if (response.requires_password_change) {
          navigate('/change-password');
          return;
        }

        // Normalize the role for consistent comparison
        const normalizedRole = response.user.role.toLowerCase().replace(/\s+/g, '_');

        // Redirect based on normalized role
        if (normalizedRole === 'program_admin') {
          navigate('/program-admin/dashboard');
        } else if (normalizedRole === 'medical_admin') {
          navigate('/medical-admin/dashboard');
        } else if (normalizedRole === 'doctor') {
          navigate('/doctor/dashboard');
        } else if (normalizedRole === 'statistician') {
          navigate('/statistician/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        if (response.requires_microsoft) {
          setError(response.message || 'This account requires Microsoft login. Please use the "Login with Microsoft" button.');
        } else {
          setError(response.message || 'Login failed');
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(error.response?.data?.error || "An error occurred during login");
    } finally {
       setLoading(false);
       setAutoLoggingIn(false);
     }
  };

  // Microsoft login handler - now uses MSAL directly
  const handleMicrosoftLogin = async (tokenOverride = null) => {
    // CAPTCHA ENABLED FOR PRODUCTION
    // STRICTLY REQUIRE CAPTCHA VERIFICATION
    const currentCaptchaToken = tokenOverride || captchaToken;
    if (!currentCaptchaToken) {
      setError("Please complete the security verification by clicking the 'Complete Security Verification' button above.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const authenticatedUser = await loginWithMicrosoft();

      if (authenticatedUser) {
        // Redirect based on user role
        const normalizedRole = typeof authenticatedUser.role === 'string'
          ? authenticatedUser.role.toLowerCase().replace(/\s+/g, '_')
          : (authenticatedUser.role?.name || '').toLowerCase().replace(/\s+/g, '_');

        let redirectPath = '/dashboard';
        switch (normalizedRole) {
          case 'doctor':
            redirectPath = '/doctor/dashboard';
            break;
          case 'medical_admin':
            redirectPath = '/medical-admin/dashboard';
            break;
          case 'program_admin':
          case 'research_admin':
            redirectPath = '/program-admin/dashboard';
            break;
          case 'statistician':
            redirectPath = '/statistician/dashboard';
            break;
          default:
            console.warn("Unknown role, using default dashboard:", authenticatedUser.role);
            redirectPath = '/dashboard';
        }

        navigate(redirectPath, { replace: true });
      } else {
        throw new Error('No user returned from Microsoft login');
      }
    } catch (error) {
      console.error("Microsoft login error:", error);
      setError(error.message || "Microsoft login failed");
    } finally {
       setLoading(false);
       setAutoLoggingIn(false);
     }
  };

  return (
    <div className="login-page">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <div className="login-card">
              <div className="row g-0">
                {/* Left Side - Image */}
                <div className="col-md-6 login-image d-none d-md-flex">
                  <div className="text-center">
                    <img
                      src={healthcareImage}
                      alt="Healthcare"
                      className="img-fluid"
                      style={{ maxWidth: "300px", borderRadius: "10px" }}
                    />
                    <h3 className="text-white mt-4 mb-3">Welcome to Health.IQ</h3>
                    <p className="text-white opacity-75">
                      Advanced Research Platform
                    </p>
                  </div>
                </div>

                {/* Right Side - Login Form */}
                <div className="col-md-6">
                  <div className="login-form">
                    <div className="text-center mb-4">
                      <img
                        src={logo}
                        alt="HealthIQ Logo"
                        style={{ height: "60px" }}
                        className="mb-3"
                      />
                      {/* Title with custom linear purple gradient as TEXT COLOR and reduced font size */}
                      <h2
                        className="login-title gradient-text"
                        style={{
                          fontWeight: 700,
                          paddingTop: "0.5rem",
                          background: "linear-gradient(135deg, #4C1788, #8E5BCC)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          color: "transparent"
                        }}
                      >
                        Sign In
                      </h2>
                      <p className="text-muted">Access your Research Dashboard</p>
                    </div>

                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      {/* Step 1: Email Entry Only */}
                      {loginStep === 'email' && (
                        <>
                          <div className="mb-3">
                            <label htmlFor="email" className="form-label">
                              Email Address
                            </label>
                            <div className="position-relative">
                              <input
                                type="email"
                                className="form-control"
                                id="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={handleEmailChange}
                                onKeyPress={handleEmailKeyPress}
                                disabled={loading}
                                required
                              />
                            </div>
                          </div>

                          <div className="d-grid">
                            <button
                              type="button"
                              className="btn"
                              onClick={handleEmailSubmit}
                              disabled={loading}
                              style={{
                                background: "linear-gradient(135deg, #4C1788, #8E5BCC)",
                                border: "none",
                                color: "white",
                                fontWeight: 600,
                              }}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Processing...
                                </>
                              ) : (
                                "Continue"
                              )}
                            </button>
                          </div>
                        </>
                      )}

                      {/* Step 2: Microsoft Login */}
                      {loginStep === 'microsoft' && (
                        <>
                          <div className="mb-3">
                            <button
                              type="button"
                              className="btn btn-outline-secondary mb-3"
                              onClick={() => {
                                setLoginStep('email');
                                setCaptchaToken(null);
                                setCaptchaError('');
                                setShowCaptchaModal(false);
                                setError('');
                              }}
                              style={{
                                borderRadius: '8px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              <i className="fas fa-arrow-left"></i>
                              Back
                            </button>
                            <div className="alert alert-info">
                              <i className="fas fa-info-circle me-2"></i>
                              <strong>Microsoft Login Required</strong><br />
                              <small>Email: {email}</small>
                            </div>
                          </div>

                          {/* CAPTCHA ENABLED FOR PRODUCTION */}
                          {/* EMR Modal Captcha for Microsoft login */}
                          <div className="mb-4">
                            {!captchaToken ? (
                              <button
                                type="button"
                                className="btn btn-security-verify w-100"
                                onClick={handleOpenCaptcha}
                                disabled={loading}
                              >
                                <i className="fas fa-shield-alt me-2"></i>
                                {loginStep === 'microsoft' ? 'Verify Security to Auto-Login' : 'Complete Security Verification'}
                              </button>
                            ) : (
                              <div className="verification-status">
                                <div className="status-icon">
                                  <i className="fas fa-check-circle text-success"></i>
                                </div>
                                <span className="status-text">
                                  {autoLoggingIn
                                    ? 'Auto-logging in...'
                                    : 'Security verification completed - Ready to auto-login'}
                                </span>
                              </div>
                            )}
                            {captchaError && (
                              <div className="alert alert-danger py-2 mt-2">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                <small>{captchaError}</small>
                              </div>
                            )}
                          </div>

                          {/* EMR Modal Captcha */}
                          <SimpleEmojiCaptcha
                            key={`captcha-modal-${showCaptchaModal ? 'open' : 'closed'}-${forceUpdate}`}
                            isVisible={showCaptchaModal}
                            onClose={handleCloseCaptcha}
                            onCaptchaComplete={handleCaptchaComplete}
                            onCaptchaError={handleCaptchaError}
                          />

                          <div className="d-grid">
                            <button
                              type="button"
                              className="microsoft-login-btn primary-microsoft-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                handleMicrosoftLogin();
                              }}
                              disabled={loading || !captchaToken}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 23 23">
                                <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                                <path fill="#f35325" d="M1 1h10v10H1z" />
                                <path fill="#81bc06" d="M12 1h10v10H12z" />
                                <path fill="#05a6f0" d="M1 12h10v10H1z" />
                                <path fill="#ffba08" d="M12 12h10v10H12z" />
                              </svg>
                              <span>
                                {autoLoggingIn
                                  ? "Auto-signing in..."
                                  : loading
                                    ? "Signing in..."
                                    : "Login with Microsoft"}
                              </span>
                            </button>
                          </div>
                        </>
                      )}

                      {/* Step 3: Email/Password Login */}
                      {loginStep === 'credentials' && (
                        <>
                          <div className="mb-3">
                            <button
                              type="button"
                              className="btn btn-outline-secondary mb-3"
                              onClick={() => {
                                setLoginStep('email');
                                setCaptchaToken(null);
                                setCaptchaError('');
                                setShowCaptchaModal(false);
                                setPassword('');
                                setError('');
                              }}
                              style={{
                                borderRadius: '8px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              <i className="fas fa-arrow-left"></i>
                              Back
                            </button>
                            <div className="alert alert-info">
                              <i className="fas fa-info-circle me-2"></i>
                              <strong>Email Login</strong><br />
                              <small>Email: {email}</small>
                            </div>
                          </div>

                          <div className="mb-4">
                            <label htmlFor="password" className="form-label">
                              Password
                            </label>
                            <div className="position-relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                id="password"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                required
                              />
                              <button
                                type="button"
                                className="btn btn-link position-absolute top-50 translate-middle-y text-decoration-none"
                                style={{ right: "10px", color: "#95a5a6" }}
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                <i
                                  className={`fas ${
                                    showPassword ? "fa-eye-slash" : "fa-eye"
                                  }`}
                                ></i>
                              </button>
                            </div>
                          </div>

                          {/* CAPTCHA ENABLED FOR PRODUCTION */}
                          {/* EMR Modal Captcha for email login */}
                          <div className="mb-4">
                            {!captchaToken ? (
                              <button
                                type="button"
                                className="btn btn-security-verify w-100"
                                onClick={handleOpenCaptcha}
                                disabled={loading || !password.trim()}
                              >
                                <i className="fas fa-shield-alt me-2"></i>
                                {password.trim() ? "Complete Security Verification to Auto-Login" : "Enter Password First"}
                              </button>
                            ) : (
                              <div className="verification-status">
                                <div className="status-icon">
                                  <i className="fas fa-check-circle text-success"></i>
                                </div>
                                <span className="status-text">
                                  {autoLoggingIn
                                    ? 'Auto-logging in...'
                                    : '✅ Security verification completed - Ready to auto-login'}
                                </span>
                              </div>
                            )}
                            {captchaError && (
                              <div className="alert alert-danger py-2 mt-2">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                <small>{captchaError}</small>
                              </div>
                            )}
                          </div>

                          {/* EMR Modal Captcha */}
                          <SimpleEmojiCaptcha
                            isVisible={showCaptchaModal}
                            onClose={handleCloseCaptcha}
                            onCaptchaComplete={handleCaptchaComplete}
                            onCaptchaError={handleCaptchaError}
                          />

                          {/* Login button - show when we have email and password */}
                          {email.trim() && password.trim() && (
                            <div className="d-grid">
                              <button
                                type="submit"
                                className="btn w-100"
                                disabled={loading || !captchaToken}
                                style={{
                                  background: captchaToken ? "linear-gradient(135deg, #28a745, #20c997)" : "#6c757d",
                                  border: "none",
                                  color: "white",
                                  fontWeight: 600,
                                  padding: "0.75rem",
                                  fontSize: "1rem"
                                }}
                              >
                                {autoLoggingIn ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    Auto-signing in...
                                  </>
                                ) : loading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    Signing in...
                                  </>
                                ) : captchaToken ? (
                                  <>
                                    <i className="fas fa-sign-in-alt me-2"></i>
                                    Sign In to Health.IQ
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-shield-alt me-2"></i>
                                    Complete Security Verification
                                  </>
                                )}
                              </button>
                            </div>
                          )}

                          {/* Show helpful message when password is entered but captcha not done */}
                          {password.trim() && !captchaToken && (
                            <div className="alert alert-info py-2">
                              <i className="fas fa-shield-alt me-2"></i>
                              <small>Complete the security verification above to auto-login</small>
                            </div>
                          )}

                          {/* Show message when password is empty */}
                          {!password.trim() && loginStep === 'credentials' && (
                            <div className="alert alert-warning py-2">
                              <i className="fas fa-key me-2"></i>
                              <small>Please enter your password to enable security verification</small>
                            </div>
                          )}
                        </>
                      )}
                    </form>

                    <div className="text-center mt-4">
                      <small className="text-muted">
                        <a href="/forgot-password" className="text-decoration-none me-3">
                          Forgot Password?
                        </a>
                        |
                        <span className="ms-3">Need help? Contact your system administrator</span>
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

